import { useEffect, useRef } from "react";

import { useAudit } from "../../../../../contexts/auditContext";
import { type WorkflowDeletion } from "../../lib/deletedWorkflowSteps";
import { type DeterministicOperationRow } from "../../lib/deterministicSteps";
import { DocumentViewerPanel } from "./DocumentViewerPanel";

interface StepCardViewerProps {
  procedureSessionId: string;
  /** The step the auditor picked, or null before they have picked one. */
  step: DeterministicOperationRow | null;
  /**
   * Whether the column's workflow still exists, and the copy for the state where
   * it does not.
   *
   * It outranks the selection rather than sitting beside it. A card is a PNG the
   * server rendered from the step record and registered as a document, and that
   * document outlives the deletion upstream — so a step still selectable from
   * Midship's verdict trail would otherwise open a picture of detail that is
   * gone, or reach the ordinary "no card for this step", which reads as a step
   * that never had one.
   *
   * The unsettled arm is why this is a state and not a flag: the review pane
   * beside this one offers its key steps for selection while the status read is
   * still out, and a viewer that drew the card in that window would be drawing
   * it off Midship's card document, which outlives a deletion — the preview the
   * status is here to replace.
   */
  workflowDeletion: WorkflowDeletion;
  onToggleStar: () => Promise<void>;
}

/**
 * The selected step's card, in the fold-out's viewer pane.
 *
 * The card is a PNG the server rendered from the step record and registered as a
 * document, so it reaches the viewer down the same path every other piece of
 * evidence does — pointed at by document id, signed on demand, drawn by the
 * shared viewer. Nothing about it is re-drawn here.
 *
 * **Which of the viewer's controls survive is decided per control, by what a
 * single-image payload can answer for:**
 *
 * - *Zoom, reset and fullscreen* go, and the card is scaled to the pane's
 *   width, scrolling if it is taller. A card is one fixed-width column
 *   rendered at twice its layout size, so scaled to the pane it reads top to
 *   bottom without panning.
 * - *The pager and search* are the PDF viewer's and never render for an image,
 *   so there is nothing to hide: a card is one page and has no text layer to
 *   search.
 * - *Annotate, and the delete a drawn box offers*, go, via `allowEditing`. The
 *   card is the run's own account of a step; an auditor drawing on it would be
 *   annotating a rendering rather than the evidence it describes, and the mark
 *   would outlive nothing, since a re-render replaces the picture.
 * - *Add Citation and Refine with AI* go with it, for the same reason and one
 *   more: citing a card would attach evidence to a picture of a step rather than
 *   to the data the step read.
 * - *Tickmarks* go. They annotate citation boxes over a PDF, and a card carries
 *   no boxes at all.
 * - *The screenshot/source toggle* goes. It exists for evidence that has both a
 *   rendered view and a live document; a card has only itself.
 * - *The include-in-workpaper star* stays, where the trail cites the card. It
 *   is the same flag the row's star writes and the review pane's key-steps
 *   section reads, so the auditor can press it wherever they happen to be
 *   looking.
 *
 * **Where the workflow was deleted the pane states that instead, whatever is
 * selected.** The deletion takes the step record each card pictures, so every
 * control above is moot, and the selection Midship's verdict trail still
 * permits opens nothing.
 */
export function StepCardViewer({
  procedureSessionId,
  step,
  workflowDeletion,
  onToggleStar,
}: StepCardViewerProps) {
  const { selectedDocument, updateCurrentDocument } = useAudit();
  const card = step?.card ?? null;
  const isWorkflowIntact = workflowDeletion.kind === "intact";
  // Withheld until the workflow's fate is known, and taken back if it stops
  // being intact, so the audit context is never pointed at a card this pane is
  // about to say has no preview. The effects are the one part of this component
  // that outlives its own render.
  const documentId = isWorkflowIntact ? (card?.documentId ?? null) : null;
  const fileName = card?.fileName;
  const placedDocumentIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (documentId === null) {
      return;
    }
    placedDocumentIdRef.current = documentId;
    void updateCurrentDocument({
      id: documentId,
      procedureSessionId,
      fileName,
      fileType: "png",
    });
  }, [documentId, fileName, procedureSessionId, updateCurrentDocument]);

  // The audit context is shared by the whole page, so only the card this pane
  // placed is taken back, and only while nothing else has replaced it; clearing
  // also retires a signing fetch still in flight for it.
  useEffect(() => {
    const placedDocumentId = placedDocumentIdRef.current;
    if (isWorkflowIntact || placedDocumentId === null) {
      return;
    }
    placedDocumentIdRef.current = null;
    if (selectedDocument?.id === placedDocumentId) {
      void updateCurrentDocument(null);
    }
  }, [isWorkflowIntact, selectedDocument, updateCurrentDocument]);

  if (workflowDeletion.kind === "unsettled") {
    return (
      <div className="flex h-full items-center justify-center px-4 text-sm text-font-secondary">
        Loading this run's evidence…
      </div>
    );
  }

  if (workflowDeletion.kind === "deleted") {
    const { copy } = workflowDeletion;
    return (
      <div className="flex h-full flex-col">
        {/* The header names the workflow where the file name of a card would
            otherwise stand, so the pane says whose evidence is missing rather
            than only that some is. */}
        <div className="shrink-0 border-b border-stone-200 px-4 py-2 text-xs text-font-secondary">
          {copy.previewHeader}
        </div>
        <div className="flex flex-1 items-center justify-center px-6">
          <div className="flex max-w-md flex-col gap-1 text-center">
            <p className="text-sm font-medium text-font-default">
              {copy.previewTitle}
            </p>
            <p className="text-sm text-font-secondary">{copy.previewBody}</p>
          </div>
        </div>
      </div>
    );
  }

  if (step === null) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-sm text-font-secondary">
        Select a step to see its card.
      </div>
    );
  }

  if (card === null) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-sm text-font-secondary">
        No card for this step
      </div>
    );
  }

  return (
    <DocumentViewerPanel
      showDocumentViewer
      // The step rather than the card's file name, which only repeats the step
      // name with a suffix and an extension.
      documentLabel={step.stepName.trim() === "" ? undefined : step.stepName}
      fitImageToWidth
      // Not a box on the card — the viewer keys its star off whichever citation
      // is active, and this step's card citation is the one the star belongs to.
      currentBoundingBox={{ citationId: step.citation?.id }}
      viewerBoundingBoxes={undefined}
      selectedBoundingBoxId={undefined}
      selectedBoundingBoxIndex={undefined}
      currentSelectionValue={undefined}
      allCitations={step.citation === null ? [] : [step.citation]}
      allowEditing={false}
      // The star writes the include flag on the card's citation, so a step the
      // trail cites no card for has nothing for a press to land on.
      allowKeyToggle={step.citation !== null}
      viewToggle={null}
      onToggleKeyCitation={onToggleStar}
      // Unreachable while editing is off: the viewer hands both to the file
      // viewer behind `allowLinking`/`allowDeleting`, which this pane refuses.
      onLinkingComplete={() => Promise.resolve()}
      onDelete={() => Promise.resolve()}
      onSelect={() => {}}
    />
  );
}
